


















































































import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { ColumnsModel } from '@/types/models/table/ColumnsModel';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';
import MStoreTable from '@/components/shared/Table/MStoreTable.vue';
import MStoreButtons from '@/components/shared/MStoreButtons.vue';
import { Fullscreen } from '@/utils';
import { Dictionary, StickyStoreNames, StickySettingsActions, StickySettingsGetters, StickySettingsFields } from '@/types';

export default Vue.extend({
  name: 'MStoreGroupedTable',
  props: {
    headerText: {
      type: String,
      required: true
    },
    errorMessage: {
      type: String,
      required: false
    },
    hasError: {
      type: Boolean,
      required: false
    },
    showHideFilters: {
      type: Boolean,
      required: false,
      default: false
    },
    items: {
      type: Array,
      required: true
    },
    fields: {
      type: Array,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: true
    },
    emptyText: {
      type: String,
      required: false
    },
    canHoverOnTableRow: {
      type: Boolean,
      required: false,
      default: true
    },
    rowClass: {
      type: Function,
      required: false
    },
    theadClass: {
      type: String,
      required: false,
      default: 'text-no-wrap'
    },
    tableId: {
      type: String,
      required: false
    },
    emitFilters: {
      type: Boolean,
      required: false,
      default: false
    },
    showFilters: {
      type: Boolean,
      required: false,
      default: false
    },
    currentPage: {
      type: Number,
      required: false,
      default: 1
    },
    itemsPerPage: {
      type: Number,
      required: false,
      default: 10
    },
    searchQuery: {
      type: String,
      required: false,
      default: ''
    },
    sortBy: {
      type: String,
      required: false,
      default: ''
    },
    sortDesc: {
      type: Boolean,
      required: false,
      default: false
    },
    emptyHtml: {
      type: String,
      required: false,
      default: ''
    },
    pkField: {
      type: String,
      required: false
    },
    selectedPk: {
      required: false
    },
    groupByField: {
      type: String,
      required: true
    },
    groupColumns: {
      type: Array,
      required: false
    },
    detailColumns: {
      type: Array,
      required: true
    },
    actions: {
      type: Array,
      required: false
    },
    detailActionButtons: {
      type: Array,
      required: false,
      default: undefined
    },
    multiSelect: {
      type: Boolean,
      required: false,
      default: false
    },
    addActionHeader: {
      type: Boolean,
      required: false,
      default: false
    },
    selectAllRowsInitially: {
      type: Boolean,
      required: false,
      default: false
    },
    allowWallboardMode: {
      type: Boolean,
      default: false
    },
    wallboardRefreshIntervalInSeconds: {
      type: Number,
      default: 0
    },
    debounce: {
      type: Number
    },
    pinFilter: Boolean
  },
  data() {
    return {
      showModal: false,
      postIsLoading: false,
      selectedRows: [] as any,
      isFullscreen: false
    };
  },
  computed: {
    ...mapGetters(StickyStoreNames.MStoreGroupedTable, [StickySettingsGetters.GetStickyStateField]),
    groupItems(this: any) {
      const groupedData = this.items.reduce((itemGroups: any, item: any) => {
        itemGroups[item[this.groupByField].toLowerCase()] = itemGroups[item[this.groupByField].toLowerCase()] || [];
        itemGroups[item[this.groupByField].toLowerCase()].push(item);
        return itemGroups;
      }, {});
      return Object.keys(groupedData).map((key: any) => {
        return Object.assign(
          {
            childRows: groupedData[key],
            _showDetails: this.getExpandedGroups(this.tableId)?.includes(key)
          },
          groupedData[key][0]
        );
      });
    },
    groupFields(this: any): ColumnsModel[] {
      const columns = this.fields.filter((x: ColumnsModel) => (this.groupColumns?.length > 0 ? this.groupColumns.includes(x.key) : !this.detailColumns.includes(x.key))) as ColumnsModel[];

      columns.unshift({
        key: 'rowExpand',
        label: '',
        sortable: false,
        thStyle: 'width: 2%'
      } as ColumnsModel);

      const ifActionColumn = columns.some((x: ColumnsModel) => x.key === 'actions');
      if ((this.actions?.length > 0 && !ifActionColumn) || this.addActionHeader) {
        columns.push({
          key: 'actions',
          label: '',
          sortable: false,
          thStyle: 'width: 20%'
        } as ColumnsModel);
      }

      return columns;
    },
    detailFieldColumns(this: any): ColumnsModel[] {
      return this.fields.filter((x: ColumnsModel) => this.detailColumns.indexOf(x.key) !== -1) as ColumnsModel[];
    },
    getLastExpandedGroups(this: any): Dictionary<string[]> {
      return this.getStickyStateField(StickySettingsFields.LastExpandedGroups);
    }
  },
  methods: {
    ...mapActions(StickyStoreNames.MStoreGroupedTable, [StickySettingsActions.SetStickyStateField]),
    onButtonClicked(this: any, event: any, row: any) {
      const selectedRows = !this.multiSelect ? row.item.childRows : this.selectedRows.filter((childRow: any) => childRow[this.groupByField] === row.item[this.groupByField]);
      if (selectedRows?.length > 0) {
        this.$emit('group-action', event, selectedRows);
      }
    },
    onRowSelect(this: any, checked: any, row: any, rows: any[]) {
      if (checked) {
        this.selectedRows.push(row);
      } else {
        const index = this.selectedRows.findIndex((childRow: any) => childRow[this.pkField] === row[this.pkField]);
        this.selectedRows.splice(index, 1);
      }
    },
    initialize(this: any) {
      this.selectedRows = [] as any;
      if (this.selectAllRowsInitially) {
        this.groupItems.forEach((group: any) => {
          this.selectedRows = [...this.selectedRows, ...group.childRows];
        });
      }
    },
    isRowSelected(this: any, row: any): boolean {
      return this.containsRow(this.selectedRows, row);
    },
    toggleDetails(row: any) {
      const value = row.item[this.groupByField].toLowerCase();
      if (row.item._showDetails === true) {
        this.removeExpandedGroup({ stateKey: this.tableId, value });
      } else {
        this.addExpandedGroup({ stateKey: this.tableId, value });
      }
      row.toggleDetails();
    },
    onRowSelectionChanged(this: any, checked: any, rows: any[]) {
      this.deselectRows(rows);
      if (checked) {
        this.selectedRows = [...this.selectedRows, ...rows];
      }
    },
    allChildRowsSelectedWithinGroup(this: any, row: any): boolean {
      return row.item.childRows.every((childRow: any) => this.containsRow(this.selectedRows, childRow));
    },
    deselectRows(this: any, rows: any[]) {
      this.selectedRows = this.selectedRows.filter((childRow: any) => !this.containsRow(rows, childRow));
    },
    containsRow(rows: any[], row: any): boolean {
      return rows.some((childRow: any) => childRow[this.pkField] === row[this.pkField]);
    },
    handleOnFullscreenChange(this: any) {
      this.isFullscreen = Fullscreen.isFullscreen();
    },
    getExpandedGroups(this: any, tableId: string) {
      return this.getStickyStateField(`${StickySettingsFields.LastExpandedGroups}.${tableId}`);
    },
    addExpandedGroup(this: any, payload: any) {
      const lastExpandedGroups = this.getLastExpandedGroups;
      if (lastExpandedGroups[payload.stateKey]) {
        lastExpandedGroups[payload.stateKey].push(payload.value);
      } else {
        lastExpandedGroups[payload.stateKey] = [payload.value];
      }
      this.setStickyStateField({ field: StickySettingsFields.LastExpandedGroups, value: lastExpandedGroups });
    },
    removeExpandedGroup(this: any, payload: any) {
      const lastExpandedGroups = this.getLastExpandedGroups;
      const indexPosition = lastExpandedGroups[payload.stateKey].findIndex((x: any) => x === payload.value);
      if (indexPosition > -1) {
        lastExpandedGroups[payload.stateKey].splice(indexPosition, 1);
      }
      this.setStickyStateField({ field: StickySettingsFields.LastExpandedGroups, value: lastExpandedGroups });
    }
  },
  watch: {
    items(this: any) {
      this.initialize();
    }
  },
  mounted(this: any) {
    this.initialize();
  },
  components: {
    LoadingSpinner,
    MStoreButtons,
    MStoreTable
  },
  created() {
    Fullscreen.registerFullscreenChange(this.handleOnFullscreenChange);
  },
  beforeDestroy() {
    Fullscreen.removeFullscreenChange(this.handleOnFullscreenChange);
  }
});
